/**
 * LOTSOG course flashcards — collections, statuses, SRS helpers.
 */

export const LOTSOG_FLASHCARD_DECK_COLLECTION = 'flashcardDeck';
export const LOTSOG_FLASHCARD_CARDS_COLLECTION = 'flashcardCards';
export const LOTSOG_FLASHCARD_PROGRESS_COLLECTION = 'flashcardProgress';

/** @typedef {'draft'|'approved'|'archived'} LotsogFlashcardStatus */
/** @typedef {'auto'|'teacher'|'student'} LotsogFlashcardCreatedBy */

/** @typedef {'correct'|'partial'|'incorrect'|'misconception'|'no_response'} LotsogFlashcardCorrectnessTier */

/** @typedef {'voice'|'typed'|'self_grade'} LotsogFlashcardInputModality */

export const FLASHCARD_CORRECTNESS_TIERS = Object.freeze([
  'correct',
  'partial',
  'incorrect',
  'misconception',
  'no_response',
]);

export const DEFAULT_FLASHCARD_RUN_PREFS = Object.freeze({
  vocalizePrompt: false,
  answerByVoice: false,
});

export const FLASHCARD_SESSION_MAX_CARDS = 20;
export const FLASHCARD_DEFAULT_EASE = 2.5;
export const FLASHCARD_MIN_EASE = 1.3;
export const FLASHCARD_INITIAL_INTERVAL_DAYS = 1;

const MASTERY_DELTA_BY_TIER = Object.freeze({
  correct: 0.12,
  partial: -0.05,
  incorrect: -0.15,
  misconception: -0.25,
  no_response: -0.12,
});

const LATENCY_CORRECT_DELTA = Object.freeze({
  fast: 0.03,
  typical: 0,
  slow: -0.04,
});

const LATENCY_INCORRECT_DELTA = Object.freeze({
  fast: -0.05,
  typical: 0,
  slow: 0.03,
});

/**
 * @param {unknown} raw
 * @returns {LotsogFlashcardCorrectnessTier}
 */
export const normalizeFlashcardCorrectnessTier = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (FLASHCARD_CORRECTNESS_TIERS.includes(s)) return s;
  return 'incorrect';
};

/**
 * @param {number} latencyMs
 * @param {number} medianMs
 * @returns {'fast'|'typical'|'slow'}
 */
export const classifyFlashcardLatencyBand = (latencyMs, medianMs) => {
  const lat = Number(latencyMs);
  const med = Number(medianMs);
  if (!Number.isFinite(lat) || lat < 0) return 'typical';
  if (!Number.isFinite(med) || med <= 0) return 'typical';
  if (lat < med * 0.75) return 'fast';
  if (lat > med * 1.25) return 'slow';
  return 'typical';
};

/**
 * @param {LotsogFlashcardCorrectnessTier} tier
 * @param {'fast'|'typical'|'slow'} [latencyBand]
 * @returns {number}
 */
export const computeFlashcardMasteryDelta = (tier, latencyBand = 'typical') => {
  const base = MASTERY_DELTA_BY_TIER[tier] ?? MASTERY_DELTA_BY_TIER.incorrect;
  if (tier === 'correct') {
    return base + (LATENCY_CORRECT_DELTA[latencyBand] ?? 0);
  }
  if (tier === 'incorrect' || tier === 'misconception') {
    return base + (LATENCY_INCORRECT_DELTA[latencyBand] ?? 0);
  }
  return base;
};

/**
 * SM-2-lite interval update from attempt result.
 * @param {object} state
 * @param {LotsogFlashcardCorrectnessTier} tier
 * @param {number} masteryDelta
 * @returns {{ easeFactor: number, intervalDays: number, reps: number, lapses: number, requeueInSession: boolean }}
 */
export const applyFlashcardSrsUpdate = (state, tier, masteryDelta) => {
  const prevEase = Number(state?.easeFactor) || FLASHCARD_DEFAULT_EASE;
  const prevInterval = Number(state?.intervalDays) || 0;
  const reps = Number(state?.reps) || 0;
  const lapses = Number(state?.lapses) || 0;

  let easeFactor = prevEase;
  let intervalDays = prevInterval;
  const nextReps = reps + 1;
  let nextLapses = lapses;
  let requeueInSession = false;

  if (tier === 'correct') {
    easeFactor = Math.min(3, prevEase + 0.1 + Math.max(0, masteryDelta));
    if (reps === 0) intervalDays = 1;
    else if (reps === 1) intervalDays = 3;
    else intervalDays = Math.max(1, Math.round(prevInterval * easeFactor));
  } else if (tier === 'partial') {
    easeFactor = Math.max(FLASHCARD_MIN_EASE, prevEase - 0.05);
    intervalDays = 1;
  } else {
    nextLapses = lapses + 1;
    easeFactor = Math.max(FLASHCARD_MIN_EASE, prevEase - 0.2);
    intervalDays = 1;
    if (tier === 'misconception' || (tier === 'incorrect' && masteryDelta <= -0.18)) {
      requeueInSession = true;
    }
  }

  return {
    easeFactor,
    intervalDays,
    reps: nextReps,
    lapses: nextLapses,
    requeueInSession,
  };
};

/**
 * Build session queue: due first, then high lapses, then new.
 * @param {Array<{ cardId: string, hidden?: boolean, progress?: object }>} cards
 * @param {number} [maxCards]
 * @returns {string[]}
 */
export const buildFlashcardSessionQueue = (cards, maxCards = FLASHCARD_SESSION_MAX_CARDS) => {
  const now = Date.now();
  const visible = (cards || []).filter((c) => c && !c.hidden && c.cardId);
  const scored = (c) => {
    const p = c.progress || {};
    const nextReviewAt = p.nextReviewAt?.toMillis?.() ?? (p.nextReviewAt ? new Date(p.nextReviewAt).getTime() : 0);
    const lapses = Number(p.lapses) || 0;
    const reps = Number(p.reps) || 0;
    const due = !nextReviewAt || nextReviewAt <= now;
    const isNew = reps === 0;
    return { cardId: c.cardId, due, lapses, isNew };
  };
  const rows = visible.map(scored);
  const due = rows.filter((r) => r.due).sort((a, b) => b.lapses - a.lapses);
  const notDue = rows.filter((r) => !r.due && r.isNew);
  const ordered = [...due, ...notDue].map((r) => r.cardId);
  return [...new Set(ordered)].slice(0, maxCards);
};

/**
 * @param {string} lessonId
 * @param {number} index
 * @returns {string}
 */
export const buildFlashcardCardId = (lessonId, index) => {
  const base = String(lessonId || 'lesson').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 48);
  return `fc_${base}_${Number(index) || 0}`;
};
